// A follow-up's draft under the ticket's messages, as the ticket view shows Kim's suggested reply:
// the response card first, then "Use Response" opens the reply editor, which is where it is sent.

import { useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { followupsService } from '@/services/followups.service';
import { describePendingAction } from '../sentinel-workspace.helpers';
import { ReplyEditor } from '../reply-editor';
import { ResponseCard } from '../response-card';
import { RESPONSE_ORIGIN, type GeneratedResponse } from '../types';
import { followupActionError } from './followup-format';
import type { FollowupRow } from './types';

/** `canSend` off shows the draft read-only: a shadow draft, or a viewer who cannot send. */
export function FollowupComposer({ row, canSend, onChanged }: { row: FollowupRow; canSend: boolean; onChanged: () => void }) {
  const draft = row.draft!;
  // A blocked draft with no text left has nothing to show as a response: it opens to be written.
  const [editing, setEditing] = useState(canSend && !draft.text.trim());
  // Once opened the editor stays mounted, so going back to the response card keeps the edits.
  const [opened, setOpened] = useState(editing);
  const openEditor = () => {
    setOpened(true);
    setEditing(true);
  };
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const response: GeneratedResponse = {
    id: `${row.id}:${draft.receivedAt}`,
    version: 1,
    markdown: draft.text,
    confidencePct: null,
    knowledgeBaseIds: [],
    ruleIds: [],
    trace: [],
    origin: RESPONSE_ORIGIN.generate,
  };

  const send = async (markdown: string) => {
    if (!markdown.trim()) {
      setError('Write the follow-up before sending it.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await followupsService.send(row.id, markdown);
      onChanged();
    } catch (err) {
      setError(followupActionError(err, 'Could not send the follow-up.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {draft.blocked && (
        <div role="status" className="flex w-full items-start gap-2.5 rounded-2xl border border-gray-500 bg-white px-4 py-3">
          <CircleAlert className="mt-0.5 size-[14px] shrink-0 text-amber-600" />
          <p className="text-[13px] leading-[18px] text-gray-900">
            Kim's safety check blocked this follow-up: {draft.blocked.reason}.{' '}
            {!canSend ? '' : draft.text.trim() ? 'Fix it or rewrite it, then send it yourself.' : 'Write the message you want to send.'}
          </p>
        </div>
      )}
      {/* Send runs these too, so the Sentinel sees them before pressing it. */}
      {draft.actions && draft.actions.length > 0 && (
        <div className="rounded-2xl border border-gray-500 bg-white px-4 py-3 text-[12px] leading-4 text-text-body">
          <p className="font-medium text-gray-700">Sending also</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {draft.actions.map((action, index) => {
              const { label, value } = describePendingAction(action);
              return (
                <li key={`${action.tool}-${index}`}>
                  {label}
                  {value && <span className="text-gray-700"> · {value}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {row.mode === 'shadow' && (
        <p className="px-1.5 text-[12px] text-gray-800">Shadow mode: this draft is for review and will not be sent.</p>
      )}
      {opened && (
        <div hidden={!editing}>
          <ReplyEditor
            editorKey={response.id}
            draftMarkdown={draft.text}
            onBackToResponse={draft.text.trim() ? () => setEditing(false) : undefined}
            onSend={(markdown) => void send(markdown)}
            sending={sending}
            closeEnabled={false}
          />
        </div>
      )}
      {!editing && draft.text.trim() && (
        <ResponseCard response={response} refineDisabled onRefine={() => undefined} onUse={canSend ? openEditor : undefined} />
      )}
      {error && (
        <p role="alert" className="px-1.5 text-[12px] text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
